import { useTranslation } from 'react-i18next';
import { Flex, Grid, Spacer, Typography } from '@aircall/tractor-v2';
import {
  AIRCALL_AUTHENTICATION_UI_URL,
  AIRCALL_DASHBOARD_URL,
} from '@constants/environment.constants';
import { ExternalLink } from '@components/Link';

import { ReactComponent as BackgroundSvg } from '@assets/images/unified-login/background.svg';
import { ReactComponent as ConversationCenterSvg } from '@assets/images/unified-login/conversation-center.svg';
import { ReactComponent as RecordingSvg } from '@assets/images/unified-login/recording.svg';
import { ReactComponent as SampleCallSvg } from '@assets/images/unified-login/sample-call.svg';
import { ReactComponent as TalkRatioSvg } from '@assets/images/unified-login/talk-ratio.svg';
import { ReactComponent as TranscriptSvg } from '@assets/images/unified-login/transcript.svg';
import { BackgroundCover } from '@components/LoginLayout/Login.styles';
import { useState } from 'react';
import { UnifiedLoginPageProps } from './UnifiedLogin.decl';
import { ScalableAircallLogo, LoginActionButton } from './UnifiedLogin.styles';
import { checkIsUrlFromDomains, isPreviewDomain } from '@helpers/url/url.helpers';
import { getRedirectWhitelist } from '@hooks/useLoginRedirect';
import { SCOPE } from '@constants/auth.constants';

export default function UnifiedLoginPage({
  path = '/',
  search = '',
}: Readonly<UnifiedLoginPageProps>) {
  const { t } = useTranslation();
  const [isNavigating, setIsNavigating] = useState(false);
  const dataTestPrefix = 'unified-login';

  const handleSignIn = () => {
    // Disable the button immediately to prevent double-clicks
    setIsNavigating(true);

    const baseUrl = new URL(window.location.origin);

    /* istanbul ignore next */
    baseUrl.pathname = isPreviewDomain() ? '/' : path;
    baseUrl.search = search;

    const originalUrl = baseUrl.toString();

    try {
      const url = new URL(originalUrl);
      const isDomainWhitelisted = checkIsUrlFromDomains(url.toString(), getRedirectWhitelist());

      if (!isDomainWhitelisted) {
        throw new Error('Invalid redirect domain');
      }
    } catch {
      window.location.href = `${AIRCALL_AUTHENTICATION_UI_URL}login?redirect=${encodeURIComponent(
        AIRCALL_DASHBOARD_URL
      )}`;
      return;
    }

    /* istanbul ignore next */
    const queryParams = isPreviewDomain()
      ? `&scope=${SCOPE.REFRESH_TOKEN},${SCOPE.ID_TOKEN},${SCOPE.REFRESH_TOKEN_EXPIRATION_DATE},${SCOPE.AUTH_STRATEGY}`
      : '';

    const authUiUrl = `${AIRCALL_AUTHENTICATION_UI_URL}login?redirect=${encodeURIComponent(originalUrl)}${queryParams}`;
    window.location.href = authUiUrl;
  };

  return (
    <Grid
      gridTemplateColumns={{
        xs: '1fr',
        md: '1fr 1fr',
      }}
      h='100%'
      py={{ xs: 'l', sm: 'xxxl' }}
      px={{ xs: 20, sm: 40, xl: 80, xxl: 160 }}
      data-test={`${dataTestPrefix}-page`}
    >
      <Flex flexDirection='column' h='100%'>
        <BackgroundCover>
          <BackgroundSvg preserveAspectRatio='xMidYMid slice' width='100%' height='100%' />
        </BackgroundCover>
        <Flex alignItems='center' flex='1'>
          <Spacer
            direction='vertical'
            space='l'
            alignItems={{
              xs: 'center',
              sm: 'flex-start',
            }}
          >
            <Spacer
              direction='vertical'
              space='xxl'
              alignItems={{
                xs: 'center',
                sm: 'flex-start',
              }}
            >
              <ScalableAircallLogo />
              <Typography
                variant={{
                  xs: 'headingBoldM',
                  sm: 'headingBoldXL',
                }}
                textAlign={{
                  xs: 'center',
                  sm: 'left',
                }}
                color='text-inverted'
              >
                {t('login.unified.title')}
              </Typography>
            </Spacer>
            <LoginActionButton
              variant='primary'
              size='large'
              onClick={handleSignIn}
              disabled={isNavigating}
              data-test={`${dataTestPrefix}-button`}
            >
              <Typography variant='bodySemiboldM' color='text-inverted'>
                {t('login.unified.button')}
              </Typography>
            </LoginActionButton>
          </Spacer>
        </Flex>
        <Flex
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{
            xs: 'center',
            sm: 'flex-start',
          }}
          gap='xxs'
        >
          <Typography
            variant={{
              xs: 'bodyRegularM',
              sm: 'headingMediumS',
            }}
            whiteSpace={{
              xs: 'normal',
              sm: 'nowrap',
            }}
            color='text-inverted'
          >
            {t('login.unified.signup_link_text')}
          </Typography>
          <ExternalLink
            href={t('login.unified.signup_link_url')}
            target='_blank'
            rel='noopener noreferrer'
            variant={{
              xs: 'bodySemiboldM',
              sm: 'headingBoldS',
            }}
            whiteSpace={{
              xs: 'normal',
              sm: 'nowrap',
            }}
            color='text-inverted'
          >
            {t('login.unified.signup_link')}
          </ExternalLink>
        </Flex>
      </Flex>
      <Flex
        flexDirection='column'
        justifyContent='center'
        gridColumn='2 / 2'
        display={{
          xs: 'none',
          sm: 'flex',
        }}
      >
        <Flex flexDirection='column' gap='m'>
          <Flex gap='l'>
            <SampleCallSvg />
            <Flex flexDirection='column' justifyContent='center' gap='m'>
              <TalkRatioSvg />
              <RecordingSvg />
            </Flex>
          </Flex>
          <Flex pl='m' gap='m'>
            <ConversationCenterSvg />
            <TranscriptSvg />
          </Flex>
        </Flex>
      </Flex>
    </Grid>
  );
}
